import { useState } from "react";
import { Link } from "wouter";
import {
  Pillars,
  PILLAR_LABELS,
  PILLAR_DETAIL,
  WEIGHTS,
  FIXED_PILLARS,
  bandFor,
  BAND_TABLE,
} from "./exampleScore";

// Band ranges are 0-45 / 45-58 / 58-70 / 70-80 / 80-100 — used to size the
// "what the score means" bar segments proportionally.
const BAND_WIDTHS = [45, 13, 12, 10, 20];

export default function ScoreBreakdown({
  pillars,
  overall,
  theme = "dark",
  showHowWeScore = true,
  showScale = true,
}: {
  pillars: Pillars;
  overall: number;
  theme?: "dark" | "light";
  showHowWeScore?: boolean;
  showScale?: boolean;
}) {
  const [openInfo, setOpenInfo] = useState<keyof Pillars | null>(null);
  const band = bandFor(overall);
  const dark = theme === "dark";

  return (
    <div className="flex flex-col gap-5">
      {showHowWeScore && (
        <div className="flex justify-end">
          <Link href="/methodology" className={`text-sm font-semibold ${dark ? "text-lime" : "text-indigo"}`}>
            How we score &rarr;
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 min-[360px]:grid-cols-2">
        {(Object.keys(pillars) as (keyof Pillars)[]).map((k) => {
          const score = Math.round(pillars[k]);
          const pillarColor = bandFor(score).color;
          const isOpen = openInfo === k;
          return (
            <div
              key={k}
              className={`flex min-w-0 flex-col gap-2 rounded-2xl p-3 sm:p-4 ${dark ? "bg-navyLight" : "border border-border bg-white"}`}
            >
              <span className={`text-sm font-semibold leading-snug ${dark ? "text-[#C9D1DE]" : "text-muted"}`}>
                {PILLAR_LABELS[k]}
              </span>
              <div className="flex items-center justify-between gap-2">
                <span className={`min-w-0 text-[11px] leading-tight ${dark ? "text-[#8FA0B8]" : "text-mutedLight"}`}>
                  {Math.round(WEIGHTS[k] * 100)}% of the score
                </span>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  aria-label={`What is ${PILLAR_LABELS[k]}?`}
                  onClick={() => setOpenInfo(isOpen ? null : k)}
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                    dark ? "bg-navy text-[#8FA0B8]" : "border border-border text-mutedLight"
                  }`}
                >
                  i
                </button>
              </div>
              <div className={`whitespace-nowrap font-display text-xl font-bold leading-none sm:text-2xl ${dark ? "text-white" : "text-ink"}`}>
                {score}
                <span className={`text-sm font-normal ${dark ? "text-[#8FA0B8]" : "text-mutedLight"}`}> /100</span>
              </div>
              <div className={`h-1.5 overflow-hidden rounded-full ${dark ? "bg-navy" : "bg-[#F1F0EC]"}`}>
                <div
                  className="h-1.5 rounded-full"
                  style={{ width: `${score}%`, background: pillarColor, transition: "width .3s" }}
                />
              </div>
              {FIXED_PILLARS.includes(k) && (
                <span className={`text-[10px] ${dark ? "text-[#8FA0B8]" : "text-mutedLight"}`}>
                  Fixed &mdash; a property fact, never changes.
                </span>
              )}
              {isOpen && (
                <p className={`m-0 text-xs leading-relaxed ${dark ? "text-[#C9D1DE]" : "text-muted"}`}>
                  {PILLAR_DETAIL[k]}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {showScale && <div className="flex flex-col gap-2">
        <div className={`text-xs font-bold tracking-widest ${dark ? "text-[#8FA0B8]" : "text-mutedLight"}`}>
          WHAT THE SCORE MEANS
        </div>
        <div className="relative flex h-3 w-full overflow-visible rounded-full">
          {BAND_TABLE.map((b, i) => (
            <div
              key={b.label}
              className={i === 0 ? "rounded-l-full" : i === BAND_TABLE.length - 1 ? "rounded-r-full" : ""}
              style={{ width: `${BAND_WIDTHS[i]}%`, background: b.color }}
            />
          ))}
          <span
            aria-hidden="true"
            className="absolute top-1/2 h-5 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_1px_3px_rgba(12,13,16,0.45)]"
            style={{ left: `${Math.min(100, Math.max(0, overall))}%` }}
          />
        </div>
        <div className="flex justify-between text-[10px] font-semibold">
          {BAND_TABLE.map((b) => (
            <span key={b.label} className={dark ? "text-[#8FA0B8]" : "text-mutedLight"}>
              {b.label}
            </span>
          ))}
        </div>
        <p className={`m-0 text-xs leading-relaxed ${dark ? "text-[#C9D1DE]" : "text-muted"}`}>
          Your score is <strong className={dark ? "text-white" : "text-ink"}>{overall}</strong> &mdash;{" "}
          <strong className={dark ? "text-white" : "text-ink"}>{band.label}</strong>. The same colours run through
          the six pillars above.
        </p>
      </div>}
    </div>
  );
}
